<script lang="ts" setup>
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import { watch } from 'vue';
import { onMounted, ref } from 'vue';
import ApplicationScoreBlurb from './ApplicationScoreBlurb.vue';

import type { VMenu } from 'vuetify/components';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { ErrorService } from '@/core/shared/errors/error.service';
import type { ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';
import { hasApplicationScore } from '@/core/conversations/application-score/application-score.util';
type MenuProps = InstanceType<typeof VMenu>['$props'];

const props = withDefaults(
  defineProps<{
    applicationId: number;
    fallbackScore?: number;
    jobTitle: string;
    jobId?: number;
    loadOnMount?: boolean;
    popupLocation?: MenuProps['location'];
    analyticsSource: string;
    proUserProfileId?: number;
    initiatedByOutreach?: boolean;
    initiatedByCopilot?: boolean;
  }>(),
  { loadOnMount: false },
);

const emit = defineEmits<{
  (e: 'change:pro-score', value: ATSApplicationScoringProScoring): void;
}>();

const applicationScoreService = new ApplicationScoreService();
const showScoreDialog = ref<boolean>(false);
const isLoading = ref<boolean>(true);
const hasLoadError = ref<boolean>(false);
const scoreDetails = ref<ATSApplicationScoringProScoring>();

function handleShowScoreChange(show: boolean) {
  showScoreDialog.value = show;
  if (show && !props.loadOnMount && !scoreDetails.value) {
    loadScoreDetails(props.applicationId);
  }
  if (show) {
    TrackingService.trackAction(TrackingActionName.SCORE_SUMMARY_VIEWED, {
      source: props.analyticsSource,
      applicationId: props.applicationId,
    });
  }
}

async function loadScoreDetails(applicantId: number | undefined) {
  if (!applicantId) {
    scoreDetails.value = undefined;
    return;
  }

  try {
    isLoading.value = true;
    hasLoadError.value = false;
    scoreDetails.value = await applicationScoreService.fetchScoreDetails(applicantId);
  } catch (error) {
    hasLoadError.value = true;
    ErrorService.captureException(error);
  } finally {
    // If no score details are returned, we should set the score to the fallback score
    if (!scoreDetails.value && hasApplicationScore(props.fallbackScore)) {
      scoreDetails.value = {
        score: props.fallbackScore,
        jobApplicationId: applicantId,
        summary: '',
        version: 'v1',
      };
    }

    if (scoreDetails.value) {
      emit('change:pro-score', scoreDetails.value);
    }

    isLoading.value = false;
  }
}

watch([() => props.applicationId], ([applicationId]) => loadScoreDetails(applicationId));
onMounted(() => {
  if (props.loadOnMount) {
    loadScoreDetails(props.applicationId);
  }
});
</script>

<template>
  <v-menu
    v-model="showScoreDialog"
    :close-on-content-click="false"
    :open-delay="200"
    @update:model-value="handleShowScoreChange"
    attach
    open-on-hover
    :location="(popupLocation as MenuProps['location']) || 'end bottom'"
    width="200px"
  >
    <template v-slot:activator="{ props }">
      <span v-bind="props" class="inline-block cursor-pointer">
        <slot name="hoverTrigger" :scoreDetails="scoreDetails" :scoreIsLoading="isLoading"> </slot>
      </span>
    </template>
    <template #default>
      <ApplicationScoreBlurb
        :key="applicationId"
        :score-details="scoreDetails"
        :job-title="jobTitle"
        :job-id="jobId"
        :is-loading="isLoading"
        :analytics-source="analyticsSource"
        :application-id="applicationId"
        :has-load-error="hasLoadError"
        :initiated-by-outreach="initiatedByOutreach"
        :initiated-by-copilot="initiatedByCopilot"
      />
    </template>
  </v-menu>
</template>
